import { all, call, delay, getContext, put, race, select, take, takeLatest } from 'redux-saga/effects';

import { whenPageVisible } from '../../_shared/infrastructure/page-visibility';
import { type SessionDetail, shouldAutoRefresh } from '../domain/session-detail.model';
import type { SessionDetailGateway } from '../infrastructure/session-detail.gateway';
import {
  cancelRequested,
  pauseRequested,
  pollStarted,
  pollStopped,
  resetRequested,
  resumeRequested,
  retryErrorsRequested,
  retryRequested,
} from './session-detail.actions';
import { selectSession } from './session-detail.selectors';
import { sessionDetailActions } from './session-detail.slice';
import type { MutationKind } from './session-detail.state';

/** Live-poll cadence (ms) while the session is still progressing. */
const POLL_MS = 3_000;

const DEFAULT_ERROR = 'Something went wrong. Please retry.';

/** Human message for the failure banner (no domain error union needed). */
function toMessage(e: unknown): string {
  if (e instanceof TypeError) {
    return 'Network error; please check your connection and retry.';
  }
  return e instanceof Error && e.message ? e.message : DEFAULT_ERROR;
}

// ── Initial load + live poll (one worker) ──

/**
 * WORKER: fetch the session, then keep polling every 3s while it is still
 * progressing and not stalled (`shouldAutoRefresh`). Only the first fetch flips
 * `loadStatus` to loading, so background refreshes don't blank the page.
 */
function* pollWorker(id: string) {
  const gateway: SessionDetailGateway = yield getContext('sessionDetailGateway');
  let first = true;
  while (true) {
    if (first) yield put(sessionDetailActions.loadStarted());
    try {
      const session = (yield call(gateway.getSession, id)) as SessionDetail;
      yield put(sessionDetailActions.loadSucceeded(session));
      if (!shouldAutoRefresh(session, Date.now())) return;
    } catch (e) {
      if (first) yield put(sessionDetailActions.loadFailed());
      // A background-refresh failure keeps the last-known session and stops.
      void toMessage(e);
      return;
    }
    first = false;
    yield delay(POLL_MS);
    yield call(whenPageVisible);
  }
}

/** WORKER: run the poll, cancelled by `pollStopped` (unmount or after an action). */
function* pollLifecycle(action: ReturnType<typeof pollStarted>) {
  yield race({ poll: call(pollWorker, action.payload), stop: take(pollStopped.type) });
}

// ── Header actions (pipeline mutations) ──

/**
 * Run one pipeline action against the currently-loaded session, then re-poll so
 * the refreshed status (and any resumed progress) flows back into the page.
 * `run` receives the resolved session id + gateway.
 */
function* runAction(
  kind: MutationKind,
  run: (gateway: SessionDetailGateway, id: string) => Generator,
) {
  const session = (yield select(selectSession)) as SessionDetail | null;
  if (!session) return;
  const gateway: SessionDetailGateway = yield getContext('sessionDetailGateway');
  yield put(sessionDetailActions.mutationStarted(kind));
  try {
    yield* run(gateway, session.id);
    yield put(sessionDetailActions.mutationSucceeded(kind));
    // Refresh + resume live updates (restarts the poll via takeLatest).
    yield put(pollStarted(session.id));
  } catch (e) {
    yield put(sessionDetailActions.mutationFailed({ kind, message: toMessage(e) }));
  }
}

function* pauseWorker() {
  yield* runAction('pause', function* (g, id) {
    yield call(g.pause, id);
  });
}
function* resumeWorker() {
  yield* runAction('resume', function* (g, id) {
    yield call(g.resume, id);
  });
}
function* retryWorker() {
  yield* runAction('retry', function* (g, id) {
    yield call(g.retry, id);
  });
}
function* cancelWorker() {
  yield* runAction('cancel', function* (g, id) {
    yield call(g.cancel, id);
  });
}
function* resetWorker() {
  yield* runAction('reset', function* (g, id) {
    yield call(g.reset, id);
  });
}
function* retryErrorsWorker(action: ReturnType<typeof retryErrorsRequested>) {
  yield* runAction('retryErrors', function* (g, id) {
    yield call(g.retryTargets, id, action.payload);
  });
}

/** WATCHERS. */
export function* sessionDetailSaga() {
  yield all([
    takeLatest(pollStarted.type, pollLifecycle),
    takeLatest(pauseRequested.type, pauseWorker),
    takeLatest(resumeRequested.type, resumeWorker),
    takeLatest(retryRequested.type, retryWorker),
    takeLatest(cancelRequested.type, cancelWorker),
    takeLatest(resetRequested.type, resetWorker),
    takeLatest(retryErrorsRequested.type, retryErrorsWorker),
  ]);
}
